import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Container } from 'react-bootstrap';
import { useMsal } from '@azure/msal-react';
import { jwtDecode } from 'jwt-decode';
import logger from 'Services/MyTuiProfileCustomLogger';

const getErrorDetails = (errorMessage, profile) => {
  switch (errorMessage) {
    case '401':
      return {
        icon: 'bi-shield-lock',
        title: 'Access Denied',
        description: 'Your current roles do not grant access to this application.',
        details: [
          { label: 'Name', value: profile.name },
          { label: 'Email', value: profile.email },
          { label: 'Current roles', value: profile.roles?.join(', ') || 'None' },
        ],
        suggestion:
          'Valid roles must start with "mtp". Please contact your administrator to get the correct role assigned.',
      };
    case '404':
      return {
        icon: 'bi-person-x',
        title: 'Profile Not Found',
        description: 'We could not find your profile in the placement system.',
        details: [
          { label: 'Name', value: profile.name },
          { label: 'Email', value: profile.email },
          { label: 'Roles', value: profile.roles?.join(', ') || 'None' },
        ],
        suggestion:
          'You need a valid profile in MTP to access the platform. Please contact your line manager to create your user profile.',
      };
    case '400':
      return {
        icon: 'bi-envelope-x',
        title: 'Invalid Email',
        description: 'The email associated with your account is not valid.',
        details: [
          { label: 'Name', value: profile.name },
          { label: 'Email', value: profile.email },
          { label: 'Roles', value: profile.roles?.join(', ') || 'None' },
        ],
        suggestion: 'Please contact your administrator to correct the email address on your account.',
      };
    default:
      return {
        icon: 'bi-exclamation-triangle',
        title: 'Something Went Wrong',
        description: 'An unexpected error occurred during authentication.',
        details: [
          { label: 'Name', value: profile.name },
          { label: 'Email', value: profile.email },
          { label: 'Roles', value: profile.roles?.join(', ') || 'None' },
          { label: 'Error code', value: errorMessage || 'Unknown' },
        ],
        suggestion: 'Please take a screenshot of this page and contact your administrator for assistance.',
      };
  }
};

const Unauthorized = ({ errorMessage, decodedToken, rawToken }) => {
  const { accounts } = useMsal();

  let profile = { name: 'Unknown User', email: 'unknown@email.com', roles: [] };

  if (accounts && accounts.length > 0) {
    const account = accounts[0];

    if (account.idTokenClaims) {
      profile = {
        name: account.idTokenClaims.name || account.name || 'Unknown User',
        email: account.idTokenClaims.email || account.username || 'unknown@email.com',
        roles: account.idTokenClaims.roles || [],
      };
    } else if (account.idToken) {
      try {
        const decodedToken = jwtDecode(account.idToken);
        profile = {
          name: decodedToken.name || account.name || 'Unknown User',
          email: decodedToken.email || account.username || 'unknown@email.com',
          roles: decodedToken.roles || [],
        };
      } catch (error) {
        logger.error('Error decoding token:', error);
        profile = {
          name: account.name || 'Unknown User',
          email: account.username || 'unknown@email.com',
          roles: [],
        };
      }
    } else {
      profile = {
        name: account.name || 'Unknown User',
        email: account.username || 'unknown@email.com',
        roles: [],
      };
    }
  }

  const error = getErrorDetails(errorMessage, profile);

  useEffect(() => {
    document.title = 'Mtp - Unauthorized';
  }, []);

  return (
    <Container
      fluid
      className="d-flex align-items-center justify-content-center"
      style={{ minHeight: '60vh', padding: 'var(--space-xl) var(--space-md)' }}
    >
      <div style={{ maxWidth: '520px', width: '100%' }}>
        {/* Icon */}
        <div className="text-center mb-4">
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'var(--color-error-light)',
            }}
          >
            <i className={`bi ${error.icon}`} style={{ fontSize: '2rem', color: 'var(--color-error)' }} />
          </div>
        </div>

        {/* Title */}
        <h2
          className="text-center"
          style={{
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--color-gray-900)',
            marginBottom: 'var(--space-xs)',
          }}
        >
          {error.title}
        </h2>

        <p className="text-center" style={{ color: 'var(--color-gray-600)', marginBottom: 'var(--space-lg)' }}>
          Hi {profile.name}, {error.description.toLowerCase()}
        </p>

        {/* Detail card */}
        <div
          className="glass-card"
          style={{
            padding: 'var(--space-lg)',
            marginTop: 0,
          }}
        >
          <div style={{ marginBottom: 'var(--space-md)' }}>
            <small style={{ color: 'var(--color-gray-500)', fontWeight: 'var(--font-weight-medium)' }}>DETAILS</small>
            {error.details.map((item) => (
              <p
                key={item.label}
                style={{
                  margin: 'var(--space-xs) 0 0',
                  color: 'var(--color-gray-700)',
                  fontSize: 'var(--font-size-base)',
                }}
              >
                <strong>{item.label}:</strong> {item.value}
              </p>
            ))}
          </div>

          <div>
            <small style={{ color: 'var(--color-gray-500)', fontWeight: 'var(--font-weight-medium)' }}>
              HOW TO RESOLVE
            </small>
            <p style={{ margin: 'var(--space-xs) 0 0', color: 'var(--color-gray-700)' }}>{error.suggestion}</p>
          </div>
        </div>

        {/* Error code badge */}
        <div className="text-center mt-3">
          <span
            style={{
              display: 'inline-block',
              padding: '0.25rem 0.75rem',
              borderRadius: 'var(--radius-pill)',
              background: 'var(--color-gray-100)',
              color: 'var(--color-gray-500)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            Error {errorMessage || '500'}
          </span>
        </div>

        {/* Decoded Token */}
        {decodedToken && (
          <div
            style={{
              marginTop: 'var(--space-lg)',
              padding: 'var(--space-md)',
              background: 'var(--color-gray-100)',
              borderRadius: 'var(--radius-md, 8px)',
              border: '1px solid var(--color-gray-200, #e5e7eb)',
              overflowX: 'auto',
            }}
          >
            <small
              style={{
                display: 'block',
                color: 'var(--color-gray-500)',
                fontWeight: 'var(--font-weight-medium)',
                marginBottom: 'var(--space-xs)',
              }}
            >
              DECODED AD TOKEN
            </small>
            <pre
              style={{
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                fontSize: 'var(--font-size-sm, 0.8rem)',
                color: 'var(--color-gray-700)',
              }}
            >
              {JSON.stringify(decodedToken, null, 2)}
            </pre>
          </div>
        )}

        {/* Raw Token (for copy-paste) */}
        {rawToken && (
          <div
            style={{
              marginTop: 'var(--space-md)',
              padding: 'var(--space-md)',
              background: 'var(--color-gray-100)',
              borderRadius: 'var(--radius-md, 8px)',
              border: '1px solid var(--color-gray-200, #e5e7eb)',
              overflowX: 'auto',
            }}
          >
            <small
              style={{
                display: 'block',
                color: 'var(--color-gray-500)',
                fontWeight: 'var(--font-weight-medium)',
                marginBottom: 'var(--space-xs)',
              }}
            >
              RAW TOKEN (copy and send to admin)
            </small>
            <pre
              style={{
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                fontSize: 'var(--font-size-sm, 0.75rem)',
                color: 'var(--color-gray-700)',
                userSelect: 'all',
                cursor: 'text',
              }}
            >
              {rawToken}
            </pre>
          </div>
        )}
      </div>
    </Container>
  );
};

Unauthorized.propTypes = {
  errorMessage: PropTypes.string,
  decodedToken: PropTypes.object,
  rawToken: PropTypes.string,
};

export default Unauthorized;
